/**
 * UsLiveMap — a live choropleth of the United States.
 *
 *   - Each state is shaded by how many visitors it has in the selected range
 *     (data from /overview `usStates`, passed in as a prop).
 *   - States with someone browsing RIGHT NOW get a pulsing green marker
 *     (data from /admin/analytics/live `byUsState`, polled here every 5s).
 *
 * Geometry is bundled (src/data/usStatesAlbers.json) — US-Census public-domain
 * shapes pre-projected to Albers USA (975×610, with Alaska/Hawaii insets), so
 * there's no map library or runtime projection.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import US from '../../data/usStatesAlbers.json';

// Vertex-average centroid of a path (good enough to anchor a marker).
function centroidOf(d) {
  const nums = (d.match(/-?\d+\.?\d*/g) || []).map(Number);
  let sx = 0, sy = 0, n = 0;
  for (let i = 0; i + 1 < nums.length; i += 2) { sx += nums[i]; sy += nums[i + 1]; n++; }
  return n ? [sx / n, sy / n] : [0, 0];
}

// Linear blend between two hex colors.
function mix(a, b, t) {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  const c = pa.map((x, i) => Math.round(x + (pb[i] - x) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

const EMPTY = '#eef2f6';   // slate-100, states with no traffic
const LOW = '#bfdbfe';     // blue-200
const HIGH = '#1d4ed8';    // blue-700

export default function UsLiveMap({ usStates = [] }) {
  const [live, setLive] = useState({ byUsState: [], count: 0 });
  const [hover, setHover] = useState(null);

  // Poll live US markers every 5s while visible.
  useEffect(() => {
    let alive = true;
    const load = () => api.get('/admin/analytics/live')
      .then(r => { if (alive) setLive({ byUsState: r.data.byUsState || [], count: (r.data.byUsState || []).reduce((s, x) => s + x.count, 0) }); })
      .catch(() => {});
    load();
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const centroids = useMemo(() => {
    const m = {};
    for (const [code, s] of Object.entries(US.states)) m[code] = centroidOf(s.path);
    return m;
  }, []);

  const byCode = useMemo(() => {
    const m = {};
    for (const s of usStates) m[s.code] = s;
    return m;
  }, [usStates]);

  const maxVisitors = useMemo(
    () => Math.max(1, ...usStates.map(s => Number(s.visitors) || 0)),
    [usStates]
  );

  const liveByCode = useMemo(() => {
    const m = {};
    for (const s of live.byUsState) m[s.code] = s.count;
    return m;
  }, [live]);

  const fillFor = (code) => {
    const v = Number(byCode[code]?.visitors) || 0;
    if (!v) return EMPTY;
    const t = v / maxVisitors;            // 0..1
    return mix(LOW, HIGH, Math.sqrt(t));  // sqrt so small counts stay visible
  };

  const totalUsVisitors = usStates.reduce((s, x) => s + (Number(x.visitors) || 0), 0);
  const topStates = [...usStates].sort((a, b) => b.visitors - a.visitors).slice(0, 8);

  const hovered = hover ? (byCode[hover] || { name: US.states[hover]?.name, visitors: 0, views: 0 }) : null;

  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-700">Where your US visitors are</h3>
        <div className="flex items-center gap-3 text-xs">
          {live.count > 0 && (
            <span className="flex items-center gap-1 text-blue-600 font-medium">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
              </span>
              {live.count} live in the US now
            </span>
          )}
          <span className="text-gray-400">{totalUsVisitors.toLocaleString()} US visitors</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_200px] gap-4 items-start">
        {/* Map */}
        <div className="relative">
          <svg viewBox={US.viewBox} className="w-full h-auto" style={{ maxHeight: 420 }}>
            {Object.entries(US.states).map(([code, s]) => (
              <path
                key={code}
                d={s.path}
                fill={fillFor(code)}
                stroke="#ffffff"
                strokeWidth={0.7}
                style={{ cursor: 'pointer', transition: 'fill .2s' }}
                onMouseEnter={() => setHover(code)}
                onMouseLeave={() => setHover(h => (h === code ? null : h))}
              >
                <title>{`${s.name}: ${(byCode[code]?.visitors || 0)} visitors`}</title>
              </path>
            ))}

            {/* Live pulsing markers */}
            {Object.entries(liveByCode).map(([code, count]) => {
              const c = centroids[code];
              if (!c) return null;
              return (
                <g key={'live-' + code} pointerEvents="none">
                  <circle cx={c[0]} cy={c[1]} r={6 + Math.min(count, 6)} fill="#3b82f6" opacity="0.25">
                    <animate attributeName="r" values={`${6 + Math.min(count, 6)};${12 + Math.min(count, 6)};${6 + Math.min(count, 6)}`} dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.35;0;0.35" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={c[0]} cy={c[1]} r="4" fill="#2563eb" stroke="#fff" strokeWidth="1" />
                </g>
              );
            })}
          </svg>

          {/* Hover readout */}
          {hovered && (
            <div className="absolute top-2 left-2 bg-gray-900/90 text-white text-xs rounded-lg px-3 py-2 pointer-events-none">
              <div className="font-semibold">{hovered.name}</div>
              <div className="text-gray-300">{(hovered.visitors || 0).toLocaleString()} visitors · {(hovered.views || 0).toLocaleString()} views</div>
              {liveByCode[hover] > 0 && <div className="text-blue-400">{liveByCode[hover]} live now</div>}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-400">
            <span>Fewer</span>
            <div className="h-2 w-32 rounded" style={{ background: `linear-gradient(to right, ${EMPTY}, ${LOW}, ${HIGH})` }} />
            <span>More</span>
            <span className="ml-3 flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-blue-500 inline-block" /> live now</span>
          </div>
        </div>

        {/* Top states list */}
        <div>
          <div className="text-xs font-semibold text-gray-500 mb-2">Top states</div>
          {topStates.length === 0 ? (
            <div className="text-xs text-gray-400">No US visitors yet</div>
          ) : (
            <div className="space-y-1.5">
              {topStates.map(s => (
                <div key={s.code}
                  className={'flex items-center justify-between text-sm px-2 py-1 rounded ' + (hover === s.code ? 'bg-blue-50' : '')}
                  onMouseEnter={() => setHover(s.code)} onMouseLeave={() => setHover(null)}>
                  <span className="flex items-center gap-2 text-gray-700">
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ background: fillFor(s.code) }} />
                    {s.name}
                    {liveByCode[s.code] > 0 && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 inline-block" />}
                  </span>
                  <span className="tabular-nums text-gray-500">{Number(s.visitors).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
